<script lang="ts">
    import {onMount} from 'svelte';
    import {DataHandler, type Row, type State} from '@vincjo/datatables/remote';
    import {featureFlagStore} from '$lib/stores/featureFlagStore';
    import RowsPerPage from '$lib/components/datatable/RowsPerPage.svelte';
    import Pagination from '$lib/components/datatable/Pagination.svelte';
    import {ColumnNames} from '$lib/models/alii-features/types';
    import {createSearchTableStore} from '$lib/stores/searchTableStore';
    import Status from '$lib/components/status/Status.svelte';
    import Checkbox from '$lib/components/common/checkbox/Checkbox.svelte';
    import SearchBox from '$lib/components/common/searchBox/SearchBox.svelte';
    import SortButton, {type ListItem} from '$lib/components/common/sortButton/SortButton.svelte';
    import {Icons} from '$lib/components/common/icon';

    export let clientId: number;
  
  // This endpoint cannot order by create date, so Id descending is used to get newest first
  const tableStore = createSearchTableStore('client-feature-flag', ColumnNames.Id);
  const localTableData = tableStore.get();
  let dataHandler = new DataHandler<Row>([], {
    rowsPerPage: localTableData.pageSize,
    totalRows: 1,
  });
  let selectedFilters: string[] = [];
  const rows = dataHandler.getRows();
  
  let showClientOnly: boolean = false;

  dataHandler.applySort({
    orderBy: localTableData.sort.orderBy,
    direction: localTableData.sort.direction,
  });
  localTableData.filters.kvp?.map((x) => {
    dataHandler.filter(x.value, x.key);
    selectedFilters = [...selectedFilters, x.key];
  });
  if (localTableData.filters.search) {
    dataHandler.filter(localTableData.filters.search, ColumnNames.Name);
  }

  dataHandler.onChange(async (state: State) => {
    const result = await featureFlagStore.fetchForClient(clientId, state, showClientOnly);
    const total: number = result.total > 0 ? result.total : 1;
    dataHandler.setTotalRows(total);
    return result.data;
  });
  dataHandler.invalidate();
  
  onMount(async () => {
    dataHandler.onChange(async (state: State) => {
      const result = await featureFlagStore.fetchForClient(clientId, state, showClientOnly);
      const total: number = result.total > 0 ? result.total : 1;
      dataHandler.setTotalRows(total);
      return result.data || [];
    });
    dataHandler.invalidate();
  });
  
  $: if (showClientOnly !== undefined) {
    dataHandler.invalidate();
  }
  
  function onShowClientOnlyClick(a) {
    showClientOnly = !showClientOnly;
  }

  let searchValue: string = localTableData.filters.search ?? '';
  let timeout: any;

  function onSearchValueChange() {
    clearTimeout(timeout);
    timeout = setTimeout(() => {
      // handle searching by feature id or by name
      dataHandler.clearFilters();
      dataHandler.filter(searchValue, "query");
      tableStore.updateSearch(searchValue);
      dataHandler.invalidate();
    }, 400);
  }
  
  function isStringInt(value: string): boolean {
    if (!value || !/^-?\d+$/.test(value)) {
      return false;
    }
    const parsed = Number(value);
    return Number.isSafeInteger(parsed);
  }

  let orderBy: string | undefined = localTableData.sort.orderBy;
  let sortBy: 'asc' | 'desc' = localTableData.sort.direction;
  let sortOptions: ListItem[] = [
    { icon: Icons['arrow-down'], displayName: 'Newest', orderBy: ColumnNames.Id, sortBy: 'desc' },
    { icon: Icons['arrow-up'], displayName: 'Oldest', orderBy: ColumnNames.Id, sortBy: 'asc' },
    { icon: Icons['arrow-up'], displayName: 'Name', orderBy: ColumnNames.Name, sortBy: 'asc' },
    { icon: Icons['arrow-down'], displayName: 'Name', orderBy: ColumnNames.Name, sortBy: 'desc' }
  ];

  function onItemSelect(orderBy: string, sortBy: 'asc' | 'desc') {
    dataHandler.applySort({ orderBy: orderBy, direction: sortBy });
    tableStore.updateSort(sortBy, orderBy);
    dataHandler.invalidate();
  }

</script>

<div class="ml-5 mr-5 mt-5 rounded-3xl border-2 border-surface-600 bg-white bg-opacity-90 p-4 lg:ml-0">
  <div class="flex flex-col place-content-between gap-5 xl:flex-row xl:gap-0">
    <span class="whitespace-nowrap text-sm font-medium text-black">Search feature flags for the client</span>
    <div class="flex flex-col gap-3">
      <div class="flex flex-row flex-wrap gap-3 xl:place-content-end">
        <SearchBox bind:value={searchValue} onInput={onSearchValueChange} isExpandable={true} />
        <Checkbox checked={showClientOnly} label={'Show Client Only'} onClick={() => onShowClientOnlyClick()} />
        <SortButton bind:orderBy bind:sortBy onButtonClick={() => function () {}} {onItemSelect} items={sortOptions} />
      </div>
    </div>
  </div>
  <hr class="divider my-5" />
  <div class="mx-auto flex flex-col p-4">
    <div class="table-container h-auto overflow-x-auto rounded-2xl rounded-bl-none rounded-br-none border border-b-0 border-[--tableBorder]">
      <table class="table table-hover table-auto rounded-2xl rounded-bl-none rounded-br-none lg:table-fixed">
        <thead class="!bg-[--tableHeaderBackground]">
          <tr>
            <th class="!text-md h-[39px] w-[30%] !p-5 !font-medium leading-3 text-[--tableHeaderFont]"> Name </th>
            <th class="!text-md h-[39px] w-[50%] !p-5 !font-medium leading-3 text-[--tableHeaderFont]"> Description </th>
            <th class="!text-md h-[39px] w-[10%] !p-5 !font-medium leading-3 text-[--tableHeaderFont]"> Is Enabled </th>
            <th class="!text-md h-[39px] w-[10%] !p-5 !font-medium leading-3 text-[--tableHeaderFont]"> All Clients </th>
            <th class="!text-md h-[39px] w-[10%] !p-5 !font-medium leading-3 text-[--tableHeaderFont]"> Is Client Active </th>
          </tr>
        </thead>
        <tbody>
          {#each $rows as row}
            <tr class="!border-[--tableBorder]">
              <td class="h-10 w-[30%] cursor-pointer content-center !px-5">
                <div class="text-md center block items-center overflow-hidden text-ellipsis whitespace-nowrap font-bold text-primary-500">
                  {row.featureName}
                </div>
              </td>
              <td class="w-[50%] content-center !px-5">
                <div class="text-md items-center font-normal text-primary-500">
                  {row.description}
                </div>
              </td>
              <td class="w-[10%] content-center">
                <div class="text-md flex items-center font-normal text-primary-500">
                  <Status type={row.isEnabled ? 'success' : 'error'} text={row.isEnabled ? 'Enabled' : 'Disabled'} />
                </div>
              </td>
              <td class="w-[10%] content-center">
                <div class="text-md flex items-center font-normal text-primary-500">
                  <Status type={row.isSetGlobally ? 'success' : 'error'} text={row.isSetGlobally ? 'Enabled' : 'Disabled'} />
                </div>
              </td>
              <td class="w-[10%] content-center">
                <div class="text-md flex items-center font-normal text-primary-500">
                  <Status type={row.isSetForClient ? (row.isActiveClient ? 'success' : 'error') : 'warning'} text={row.isSetForClient ? (row.isActiveClient ? 'Enabled' : 'Disabled') : 'Not Set'} />
                </div>
              </td>
            </tr>
          {/each}
        </tbody>
      </table>
    </div>
    <div class="mt-5 inline-flex w-full justify-between">
      <RowsPerPage handler={dataHandler} store={tableStore} />
      <Pagination handler={dataHandler}></Pagination>
    </div>
  </div>
</div>
